import { IAccountSettings, L } from './_api';
import { HocuspocusProvider } from '@hocuspocus/provider';
import { environment } from 'src/environments/environment';
import { YDocument, YEntity } from 'src/yjs/ydocument';
import { MouseType } from 'src/graphics/mouse';
import { Util } from 'src/util/util';

export const usercolors = [
    '#30bced', '#6eeb83', '#ffbc42', '#ecd444',
    '#ee6352', '#9ac2c9', '#8acb88', '#1be7ff',
    '#f783ac', '#ced4da', '#da77f2', '#9775fa',
    '#748ffc', '#38d9a9', '#69db7c', '#a9e34b',
    '#ffa94d', '#e5dbff', '#99e9f2', '#d8f5a2'
];

export class Account {
    public static id: string;
    public static loginName: string;
    public static provider: HocuspocusProvider;
    public static settings: YEntity<IAccountSettings>;
    public static DEFAULT_SETTINGS: IAccountSettings = {
        name: '',
        color: usercolors[Math.floor(Math.random() * usercolors.length)],
        chat: {
            alwaysOpen: false,
            autoscroll: true,
            keepOpenAfterSend: true,
            width: 400,
            zoom: 100,
        },
        dicemacros: ['d6', 'd20'],
        activeLayerId: L.Fog,
        snapping: true,
        alignment: 1,
        erasing: false,
        mousetype: MouseType.Box,
        highquality: undefined,
        swapmiddleright: false,
        requiredragselect: false,
        disableSFX: false,
        opaquemacros: false,
        clearDate: 0,
        openAiKey: '',
    };

    public static initialize(accountId: string, username: string, provider: HocuspocusProvider, displayName: string) {
        Account.id = accountId;
        Account.loginName = username;
        Account.provider = provider;
        Account.settings = new YDocument(provider.document).getEntity('settings', Account.DEFAULT_SETTINGS);

        /* Fix any busted settings */
        Account.settings.modify((settings) => {
            settings.name = Account.storeName(displayName);
            settings.highquality = settings.highquality ?? this.useHighQuality();
            for (let property in this.DEFAULT_SETTINGS) {
                settings[property] = settings[property] ?? this.DEFAULT_SETTINGS[property];

                if (property == 'chat') {
                    for (let chatProperty in this.DEFAULT_SETTINGS.chat) {
                        settings.chat[chatProperty] = settings.chat[chatProperty] ?? this.DEFAULT_SETTINGS.chat[chatProperty];
                    }
                }
            }
        });
    }

    public static getResolution() {
        if (Account.settings.get().highquality) {
            return window.devicePixelRatio == 1 ? 2 : window.devicePixelRatio;
        }

        return window.devicePixelRatio;
    }

    public static async uploadImage(file: Blob) {
        const token = await Account.provider.getToken() as string;
        const userToken = JSON.parse(token).userToken;
        let data = new FormData();
        data.append('image', file);

        const response = await fetch(`${Util.roomUrl}/images`, {
            method: 'POST',
            headers: new Headers({ 'Authorization': `Bearer ${userToken}` }),
            body: data
        });
        const filename = await response.text();
        return filename;
    }

    public static HasAiApiKey() {
        // (noten): TODO: Instead of taking keys, we should integrate with OpenAI's Oauth.
        return Account.settings.get().openAiKey !== undefined || Account.settings.get().openAiKey !== null;
    }

    public static async completion(prompt: string) {
        const openAiKey = Account.settings.get().openAiKey;
        const token = await Account.provider.getToken();
        if (!token) return;
        const user = JSON.parse(token)['userToken']
        const response = await fetch(`${environment.hostUrl}/completion`, {
            method: 'POST',
            headers: new Headers({ 'Authorization': `Bearer ${user}`, 'Content-Type': 'application/json' }),
            body: JSON.stringify({
                prompt: prompt,
                apiKey: openAiKey,
            })
        });
        const data = await response.json();
        if (data === null) return 'Request failed.';
        return JSON.parse(data);
    }

    private static useHighQuality() {
        try {
            const canvas = document.createElement('canvas');
            const gl = canvas.getContext('webgl');
            if (!gl) return false;

            const buffer = gl.createBuffer();
            gl.bindBuffer(gl.ARRAY_BUFFER, buffer);
            const array = new Float32Array(1e6);

            let total = 0;
            const samples = 5;
            for (let i = 0; i < samples; i++) {
                const t0 = performance.now();
                gl.bufferData(gl.ARRAY_BUFFER, array, gl.STATIC_DRAW);
                total += performance.now() - t0;
            }
            const average = total / samples;

            gl.deleteBuffer(buffer);
            const loseContext = gl.getExtension('WEBGL_lose_context');
            if (loseContext && typeof loseContext.loseContext === 'function') {
                loseContext.loseContext();
            }

            return average < 10;
        } catch {
            return false;
        }
    }

    public static storeName(name: string): string {
        localStorage.setItem('nickname', name);
        return name;
    }
}
